import type { CountyShare, ObligationCountyAllocation, ClientTaxRateConfig } from '../types';

/**
 * How an obligation's surtax should read on screen.
 *
 * Four states, always distinguishable — the point of the whole view slice. Before this, the
 * surtax an obligation files under was invisible on every obligation surface: the only edit path
 * was a dialog mounted on a TASK detail page, so staff had to open a task to see the rate their
 * filings compute at.
 */
export type SurtaxProvenance =
  /** Straight from the DR-15DSS county table. */
  | 'table'
  /** A staff-entered override wins over the table — worth flagging, it is a manual number. */
  | 'override'
  /** Split across counties on this obligation. */
  | 'allocated'
  /** Nothing resolves — filings CANNOT compute. Today's silent hard stop, made visible early. */
  | 'unset';

export interface SurtaxDescription {
  provenance: SurtaxProvenance;
  /** The effective (blended, where allocated) surtax rate as a fraction, or null when unset. */
  rate: number | null;
  /** Single county name, or null when allocated across several / unset. */
  county: string | null;
  /** Populated only for 'allocated'. */
  counties: CountyShare[];
  /** True when the number shown is an ESTIMATE from a share basis rather than a single rate. */
  isAllocated: boolean;
}

/**
 * Derives the display state from an obligation's allocation and the client-level fallback.
 *
 * ⚠ Rates arrive ALREADY RESOLVED from the API — this never resolves a county itself. Carrying
 * the 67-county DR-15DSS table in TypeScript would give the fleet a second copy to keep in step
 * each January, and the two would drift the first time one was updated alone.
 */
export function describeSurtax(
  allocation: ObligationCountyAllocation | null | undefined,
  blendedSurtaxRate: number | null | undefined,
  rateConfig: (ClientTaxRateConfig & { effectiveSurtaxRate?: number | null }) | null | undefined,
): SurtaxDescription {
  // An allocation, when present, WINS over the client-level config — that is the whole point of
  // making it per-obligation; if the config could win, the allocation would be decorative.
  //
  // ⚠ The guard is on the county LIST, not on the document. An allocation cleared back to zero
  // counties is a legitimate "undo the split" and must return to the client config, not read as
  // "cannot compute".
  if (allocation && allocation.counties.length > 0) {
    const single = allocation.counties.length === 1 ? allocation.counties[0] : null;
    return {
      provenance: 'allocated',
      rate: blendedSurtaxRate ?? null,
      county: single ? single.county : null,
      counties: allocation.counties,
      isAllocated: true,
    };
  }

  const resolved = rateConfig?.effectiveSurtaxRate;

  // 🔴 `null`/`undefined` mean unresolvable. `0` does NOT — Citrus and Collier are known-zero
  // counties, listed explicitly in the table precisely so they read as a rate rather than a gap.
  // A falsy check here would render "filings cannot compute" for a client that computes fine, and
  // send staff hunting a configuration problem that does not exist.
  if (resolved === null || resolved === undefined || !rateConfig?.county) {
    return {
      provenance: 'unset',
      rate: null,
      // Still named where we have it, so the warning can quote the offending value.
      county: rateConfig?.county ?? null,
      counties: [],
      isAllocated: false,
    };
  }

  const overridden = rateConfig.countySurtaxRateOverride !== null
    && rateConfig.countySurtaxRateOverride !== undefined;

  return {
    provenance: overridden ? 'override' : 'table',
    rate: resolved,
    county: rateConfig.county,
    counties: [],
    isAllocated: false,
  };
}

/**
 * A rate as a percent string. Trailing zeros are trimmed to at most two decimals so 1.5% reads as
 * "1.5%" and a blended 1.20% reads as "1.2%", while 1.25% keeps its precision.
 */
export function formatRatePercent(rate: number | null | undefined): string {
  // 🔴 An explicit null check, not a falsy one: 0 is a real rate (a known-zero county) and must
  // render as "0%", never as the em dash that means "nothing resolves".
  if (rate === null || rate === undefined) return '—';
  // toFixed(3) then Number() trims trailing zeros, so 1.5% reads "1.5%" while a blended 1.15%
  // keeps the precision it needs — rounding that to 1.2% would misstate what the filing computes.
  return `${Number((rate * 100).toFixed(3)).toString()}%`;
}

/** A share as a percent string, for the "Broward 60% / Duval 40%" summary. */
export function formatSharePercent(share: number): string {
  return `${Number((share * 100).toFixed(2)).toString()}%`;
}

/** "Broward 60% / Duval 40%" — the allocation summary line. */
export function formatAllocationSummary(counties: CountyShare[]): string {
  return counties.map((c) => `${c.county} ${formatSharePercent(c.share)}`).join(' / ');
}
